/**
 * AuthContext — global authentication state.
 * - accessToken in memory only
 * - refreshToken in localStorage
 * - user profile cached in state
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { authApi, usersApi, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true during initial hydration
  const sessionInitialized = useRef(false); // Guard against React StrictMode double-effect

  // On mount: try to restore session via refresh token
  useEffect(() => {
    // Prevent double-execution in React StrictMode (dev) which would cause
    // the same refresh token to be used twice — rotating it and making the
    // second call fail with TOKEN_INVALID, clearing the session.
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;

    (async () => {
      const rt = tokenStore.getRefresh();
      if (rt) {
        try {
          const data = await authApi.refresh(rt);
          tokenStore.setAccess(data.access_token);
          tokenStore.setRefresh(data.refresh_token);
          const profile = await usersApi.me();
          setUser(profile);
        } catch {
          tokenStore.clear();
        }
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login({ email, password });
    if (data.requires_2fa) {
      return data; // caller handles 2FA flow
    }
    tokenStore.setAccess(data.access_token);
    tokenStore.setRefresh(data.refresh_token);
    setUser(data.user);
    return data;
  }, []);

  const completeLogin = useCallback((data) => {
    tokenStore.setAccess(data.access_token);
    tokenStore.setRefresh(data.refresh_token);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    const rt = tokenStore.getRefresh();
    if (rt) {
      try { await authApi.logout(rt); } catch { /* best-effort */ }
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await usersApi.me();
      setUser(profile);
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, completeLogin, refreshUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
